import React from 'react';
import { Alert, Grid, TextField, Typography } from '@mui/material';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import Modal from '../../../components/general/modal';
import { ButtonCancel, ButtonContinue } from '../../../components/general/mini_components';

const FUTURE_SKEW_TOLERANCE_MS = 60 * 1000;

function localDateParts(date: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
}

function parseLocalDateTime(date: string, time: string) {
  if (!date || !time) {
    return null;
  }
  const parsed = new Date(`${date}T${time}`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
}

interface DischargeConfirmDialogProps extends LocalizeContextProps {
  open: boolean;
  enabled: boolean;
  patientName: string;
  patientIdLabel?: string;
  admissionDateTime?: string | null;
  now?: Date;
  onCancel: () => void;
  onContinue: (dischargeDateTime: string | null) => void;
}

function DischargeConfirmDialogBase({
  open,
  enabled,
  patientName,
  patientIdLabel,
  admissionDateTime,
  now,
  onCancel,
  onContinue,
  translate,
}: DischargeConfirmDialogProps) {
  const nowValue = now ?? new Date();
  const defaults = localDateParts(nowValue);
  const [dischargeDate, setDischargeDate] = React.useState(defaults.date);
  const [dischargeTime, setDischargeTime] = React.useState(defaults.time);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const freshNow = localDateParts(now ?? new Date());
    setDischargeDate(freshNow.date);
    setDischargeTime(freshNow.time);
  }, [open, now]);

  const validationError = React.useMemo(() => {
    if (!enabled) {
      return false;
    }
    const chosenDateTime = parseLocalDateTime(dischargeDate, dischargeTime);
    if (!chosenDateTime) {
      return true;
    }
    const chosenMs = chosenDateTime.getTime();
    if (admissionDateTime) {
      const admissionMs = new Date(admissionDateTime).getTime();
      if (!Number.isNaN(admissionMs) && chosenMs < admissionMs) {
        return true;
      }
    }
    const nowMs = (now ?? new Date()).getTime();
    return chosenMs > nowMs + FUTURE_SKEW_TOLERANCE_MS;
  }, [enabled, dischargeDate, dischargeTime, admissionDateTime, now]);

  const handleContinue = () => {
    if (!enabled) {
      onContinue(null);
      return;
    }
    const chosenDateTime = parseLocalDateTime(dischargeDate, dischargeTime);
    if (!chosenDateTime || validationError) {
      return;
    }
    onContinue(chosenDateTime.toISOString());
  };

  return (
    <Modal
      open={open}
      title={<Translate id="hospital.search_patient.modal.discharge-confirm.title" />}
      closeModal={onCancel}
    >
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="body1">
            <Translate
              id={
                enabled
                  ? 'hospital.search_patient.modal.discharge-confirm.body'
                  : 'hospital.search_patient.modal.discharge-confirm.body-no-datetime'
              }
            />
          </Typography>
          <Typography variant="body2">{patientName}</Typography>
          {patientIdLabel && <Typography variant="body2">{patientIdLabel}</Typography>}
        </Grid>
        {enabled && (
          <Grid item xs={12}>
            <Grid container spacing={2} style={{ paddingTop: '1rem' }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  variant="outlined"
                  size="small"
                  label={translate('hospital.search_patient.modal.discharge-confirm.discharge-date').toString()}
                  value={dischargeDate}
                  onChange={(event) => setDischargeDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  data-testid="discharge-date"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  variant="outlined"
                  size="small"
                  label={translate('hospital.search_patient.modal.discharge-confirm.discharge-time').toString()}
                  value={dischargeTime}
                  onChange={(event) => setDischargeTime(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  data-testid="discharge-time"
                />
              </Grid>
              {validationError && (
                <Grid item xs={12}>
                  <Alert severity="error" data-testid="discharge-datetime-error">
                    <Translate id="hospital.search_patient.modal.discharge-confirm.discharge-datetime-range-error" />
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
        <Grid item xs={12} style={{ paddingTop: '1rem' }}>
          <ButtonCancel onClick={onCancel} data-testid="cancel-modal" color="primary" spaceright={1}>
            <Translate id="general.cancel" />
          </ButtonCancel>
          &nbsp;
          <ButtonContinue
            onClick={handleContinue}
            data-testid="continue-modal"
            color="green"
            disabled={enabled && validationError}
          >
            <Translate id="general.continue" />
          </ButtonContinue>
        </Grid>
      </Grid>
    </Modal>
  );
}

export const DischargeConfirmDialog = withLocalize(DischargeConfirmDialogBase);
